// @ts-nocheck
import React from 'react';
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import {
  useOrganization,
  useOrganizationMembers,
  useOrganizationUsage,
  useTeamMembers,
  useFeedbackCycles,
  useDashboardAnalytics,
  useUpdateOrganization,
  useUploadOrganizationLogo,
  useRemoveOrganizationLogo,
  useCreateBillingPortal,
  useCreatePaymentLink,
  useCreateCheckoutSession
} from "@/hooks/api-hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlanUpgradeModal, type PlanId } from '@/components/modals/PlanUpgradeModal';
import {
  Save,
  Building,
  Users,
  UserPlus,
  Upload,
  Trash,
  Calendar,
  MessageSquare,
  TrendingUp,
  CreditCard,
  Check,
  Shield,
  Settings,
  DollarSign
} from "lucide-react";
import { getInitials, getAvatarColor, resolveAssetUrl } from "@/lib/utils";

const organizationSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  industry: z.string().optional(),
  companySize: z.string().optional(),
  timeZone: z.string().optional(),
  website: z.string().url().optional().or(z.literal("")),
  description: z.string().optional(),
  logo: z.any().optional(),
});

const feedbackConfigSchema = z.object({
  anonymousFeedback: z.boolean(),
});

const auditConfigSchema = z.object({
  auditEnabled: z.boolean(),
  auditRetentionDays: z.number().min(30).max(2555), // 30 days to 7 years
  auditDetailLevel: z.enum(['basic', 'detailed', 'comprehensive']),
});

const LOGO_ALLOWED_TYPES = ['image/png', 'image/svg+xml'];
const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

type OrganizationFormValues = z.infer<typeof organizationSchema>;
type FeedbackConfigValues = z.infer<typeof feedbackConfigSchema>;
type AuditConfigValues = z.infer<typeof auditConfigSchema>;

const isLikelyImageUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  return (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('/') ||
    trimmed.startsWith('data:image')
  );
};

const extractLogoUrl = (payload: unknown): string | null => {
  if (!payload) {
    return null;
  }

  const visited = new Set<unknown>();
  const queue: unknown[] = [];

  const enqueue = (value: unknown) => {
    if (!value || visited.has(value)) {
      return;
    }
    visited.add(value);
    queue.push(value);
  };

  enqueue(payload);

  while (queue.length > 0) {
    const candidate = queue.shift();
    if (!candidate) {
      continue;
    }

    if (typeof candidate === 'string') {
      if (isLikelyImageUrl(candidate)) {
        return candidate;
      }
      continue;
    }

    if (candidate instanceof File) {
      continue;
    }

    if (typeof candidate === 'object') {
      const dataObj = candidate as Record<string, unknown>;

      const directData = 'data' in dataObj ? dataObj.data : undefined;
      if (directData) {
        enqueue(directData);
      }

      const possibleKeys = [
        'url',
        'logoUrl',
        'logo_url',
        'avatar',
        'avatarUrl',
        'avatar_url',
        'logo',
        'image',
        'imageUrl',
        'image_url',
        'path',
        'src',
        'secureUrl',
        'secure_url',
      ];

      for (const key of possibleKeys) {
        if (!(key in dataObj)) {
          continue;
        }

        const value = dataObj[key];
        if (typeof value === 'string' && isLikelyImageUrl(value)) {
          return value;
        }

        if (value && typeof value === 'object') {
          const nestedUrl = (value as any).url ?? (value as any).secureUrl ?? (value as any).secure_url;
          if (typeof nestedUrl === 'string' && isLikelyImageUrl(nestedUrl)) {
            return nestedUrl;
          }
          enqueue(value);
        }
      }

      for (const value of Object.values(dataObj)) {
        if (typeof value === 'string' && isLikelyImageUrl(value)) {
          return value;
        }
        if (value && typeof value === 'object') {
          enqueue(value);
        }
      }
    }
  }

  return null;
};

const industries = [
  { value: "technology", label: "Technology" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "education", label: "Education" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail" },
  { value: "other", label: "Other" },
];

const companySizes = [
  { value: "1-10", label: "1-10 employees" },
  { value: "11-50", label: "11-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-500", label: "201-500 employees" },
  { value: "500+", label: "500+ employees" },
];

const timeZones = [
  // Americas
  { value: "America/Los_Angeles", label: "Pacific Time (PST/PDT)" },
  { value: "America/Denver", label: "Mountain Time (MST/MDT)" },
  { value: "America/Chicago", label: "Central Time (CST/CDT)" },
  { value: "America/New_York", label: "Eastern Time (EST/EDT)" },
  { value: "America/Halifax", label: "Atlantic Time (AST/ADT)" },
  { value: "America/St_Johns", label: "Newfoundland Time (NST/NDT)" },
  { value: "America/Sao_Paulo", label: "Brasília Time (BRT)" },
  { value: "America/Argentina/Buenos_Aires", label: "Argentina Time (ART)" },
  { value: "America/Lima", label: "Peru Time (PET)" },
  { value: "America/Mexico_City", label: "Central Mexico Time (CST)" },
  { value: "America/Santiago", label: "Chile Time (CLT)" },

  // Europe
  { value: "Europe/London", label: "Greenwich Mean Time (GMT/BST)" },
  { value: "Europe/Dublin", label: "Irish Standard Time (IST)" },
  { value: "Europe/Paris", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Berlin", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Rome", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Madrid", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Amsterdam", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Zurich", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Stockholm", label: "Central European Time (CET/CEST)" },
  { value: "Europe/Helsinki", label: "Eastern European Time (EET/EEST)" },
  { value: "Europe/Athens", label: "Eastern European Time (EET/EEST)" },
  { value: "Europe/Moscow", label: "Moscow Standard Time (MSK)" },
  { value: "Europe/Istanbul", label: "Turkey Time (TRT)" },

  // Asia
  { value: "Asia/Dubai", label: "Gulf Standard Time (GST)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "Asia/Dhaka", label: "Bangladesh Standard Time (BST)" },
  { value: "Asia/Jakarta", label: "Western Indonesia Time (WIB)" },
  { value: "Asia/Bangkok", label: "Indochina Time (ICT)" },
  { value: "Asia/Singapore", label: "Singapore Standard Time (SGT)" },
  { value: "Asia/Manila", label: "Philippine Standard Time (PST)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong Time (HKT)" },
  { value: "Asia/Shanghai", label: "China Standard Time (CST)" },
  { value: "Asia/Taipei", label: "Taiwan Standard Time (TST)" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
  { value: "Asia/Seoul", label: "Korea Standard Time (KST)" },
  { value: "Asia/Vladivostok", label: "Vladivostok Time (VLAT)" },

  // Africa
  { value: "Africa/Cairo", label: "Eastern European Time (EET)" },
  { value: "Africa/Lagos", label: "West Africa Time (WAT)" },
  { value: "Africa/Johannesburg", label: "South Africa Standard Time (SAST)" },
  { value: "Africa/Nairobi", label: "East Africa Time (EAT)" },
  { value: "Africa/Casablanca", label: "Western European Time (WET)" },

  // Oceania
  { value: "Australia/Sydney", label: "Australian Eastern Time (AEST/AEDT)" },
  { value: "Australia/Melbourne", label: "Australian Eastern Time (AEST/AEDT)" },
  { value: "Australia/Brisbane", label: "Australian Eastern Standard Time (AEST)" },
  { value: "Australia/Perth", label: "Australian Western Standard Time (AWST)" },
  { value: "Australia/Adelaide", label: "Australian Central Time (ACST/ACDT)" },
  { value: "Pacific/Auckland", label: "New Zealand Standard Time (NZST/NZDT)" },
  { value: "Pacific/Fiji", label: "Fiji Time (FJT)" },
  { value: "Pacific/Honolulu", label: "Hawaii-Aleutian Standard Time (HST)" },

  // UTC
  { value: "UTC", label: "Coordinated Universal Time (UTC)" },
];

export function OrganizationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [planUpgradeOpen, setPlanUpgradeOpen] = React.useState(false);
  const [isOrganizationSaving, setIsOrganizationSaving] = React.useState(false);
  const [isFeedbackSaving, setIsFeedbackSaving] = React.useState(false);
  const [isAuditSaving, setIsAuditSaving] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<string>("general");

  // Check permissions - only OWNER can access organization settings
  const canManageOrganization = user?.organizationRole === 'owner' || user?.role === 'OWNER';

  // Early return if user doesn't have access - show 403 page
  if (user && !canManageOrganization) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto">
          <div className="mx-auto w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mb-6">
            <Building className="w-12 h-12 text-orange-600" />
          </div>
          <div className="text-6xl font-bold text-muted-foreground mb-4">403</div>
          <h2 className="text-2xl font-bold mb-4">Access Forbidden</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access organization settings. This feature is only available to organization owners.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Return to Dashboard
            </Button>
            <Button onClick={() => navigate(-1)} variant="outline" className="w-full">
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Fetch organization data
  const { data: organizationData, isLoading: orgLoading } = useOrganization(
    user?.currentOrganizationId || ''
  );
  const { data: membersData, isLoading: membersLoading } = useOrganizationMembers(
    user?.currentOrganizationId || ''
  );
  const { data: organizationUsageData } = useOrganizationUsage(
    user?.currentOrganizationId || ''
  );
  const { data: teamMembersData } = useTeamMembers();
  const { data: feedbackCyclesData } = useFeedbackCycles();
  const { data: analyticsData } = useDashboardAnalytics();

  const organization = organizationData?.data;
  const members = membersData?.data || [];
  const activeMembers = React.useMemo(
    () => members.filter((member: any) => !member?.isInvitation),
    [members]
  );
  const pendingInvites = React.useMemo(
    () => members.filter((member: any) => member?.isInvitation),
    [members]
  );
  const totalMembersCount = members.length;
  const activeMembersCount = activeMembers.length;
  const pendingInvitesCount = pendingInvites.length;
  const managerCount = React.useMemo(
    () =>
      activeMembers.filter((member: any) =>
        ['owner', 'admin', 'manager'].includes((member?.role ?? '').toLowerCase())
      ).length,
    [activeMembers]
  );
  const recentMembers = React.useMemo(() => {
    return [...members]
      .sort((a: any, b: any) => {
        const aDate = a?.joinedAt ? new Date(a.joinedAt).getTime() : 0;
        const bDate = b?.joinedAt ? new Date(b.joinedAt).getTime() : 0;
        return bDate - aDate;
      })
      .slice(0, 3);
  }, [members]);
  const teamMembers = teamMembersData?.data || [];
  const feedbackCycles = feedbackCyclesData?.data || [];
  const analytics = analyticsData?.data;

  const formatRoleLabel = (role?: string | null) => {
    if (!role) {
      return 'Member';
    }
    const normalized = String(role);
    return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
  };

  const getMemberTimelineLabel = (member: any) => {
    if (!member?.joinedAt) {
      return '';
    }
    const dateLabel = new Date(member.joinedAt).toLocaleDateString();
    return member?.isInvitation ? `Invited ${dateLabel}` : `Joined ${dateLabel}`;
  };

  const [organizationLogo, setOrganizationLogo] = React.useState<string | null>(() =>
    resolveAssetUrl(extractLogoUrl(organization))
  );
  const [logoPreviewUrl, setLogoPreviewUrl] = React.useState<string | null>(null);
  const [isLogoUploading, setIsLogoUploading] = React.useState(false);
  const [isLogoRemoving, setIsLogoRemoving] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const logoObjectUrlRef = React.useRef<string | null>(null);

  const organizationForm = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationSchema),
    defaultValues: {
      name: organization?.name || '',
      industry: organization?.industry || '',
      companySize: organization?.companySize || '',
      timeZone: organization?.timeZone || '',
      website: organization?.website || '',
      description: organization?.description || '',
    },
  });

  const feedbackForm = useForm<FeedbackConfigValues>({
    resolver: zodResolver(feedbackConfigSchema),
    defaultValues: {
      anonymousFeedback: organization?.anonymousFeedback ?? true,
    },
  });

  const auditForm = useForm<AuditConfigValues>({
    resolver: zodResolver(auditConfigSchema),
    defaultValues: {
      auditEnabled: organization?.auditEnabled ?? true,
      auditRetentionDays: organization?.auditRetentionDays ?? 90,
      auditDetailLevel: organization?.auditDetailLevel ?? 'detailed',
    },
  });

  // Reset form values when organization data is loaded
  React.useEffect(() => {
    if (organization) {
      organizationForm.reset({
        name: organization.name || '',
        industry: organization.industry || '',
        companySize: organization.companySize || '',
        timeZone: organization.timeZone || '',
        website: organization.website || '',
        description: organization.description || '',
      });

      feedbackForm.reset({
        anonymousFeedback: organization.anonymousFeedback ?? true,
      });

      auditForm.reset({
        auditEnabled: organization.auditEnabled ?? true,
        auditRetentionDays: organization.auditRetentionDays ?? 90,
        auditDetailLevel: organization.auditDetailLevel ?? 'detailed',
      });

      const resolvedLogo = resolveAssetUrl(extractLogoUrl(organization));
      if (resolvedLogo) {
        setOrganizationLogo(resolvedLogo);
        if (logoPreviewUrl) {
          setLogoPreviewUrl(null);
        }
      } else if (!logoPreviewUrl) {
        setOrganizationLogo(null);
      }
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = null;
      }
    }
  }, [organization, organizationForm, feedbackForm, auditForm, user?.email, logoPreviewUrl]);

  React.useEffect(() => {
    return () => {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = null;
      }
    };
  }, []);

  const updateOrganizationMutation = useUpdateOrganization();
  const uploadLogoMutation = useUploadOrganizationLogo();
  const removeLogoMutation = useRemoveOrganizationLogo();
  const createBillingPortalMutation = useCreateBillingPortal();
  const createPaymentLinkMutation = useCreatePaymentLink();
  const createCheckoutSessionMutation = useCreateCheckoutSession();

  const onSubmitOrganization = async (data: OrganizationFormValues) => {
    setIsOrganizationSaving(true);
    try {
      const result = await updateOrganizationMutation.mutateAsync(data);
      const wasSkipped = Boolean((result as any)?.skipped);

      if (wasSkipped) {
        alert('No changes detected. Update a supported field and try again.');
        return;
      }

      alert('Organization settings saved successfully!');
    } catch (error) {
      console.error("Organization update error:", error);
      alert(`Failed to save organization settings: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsOrganizationSaving(false);
    }
  };

  const onSubmitFeedbackConfig = async (data: FeedbackConfigValues) => {
    setIsFeedbackSaving(true);
    try {
      const result = await updateOrganizationMutation.mutateAsync(data);
      const wasSkipped = Boolean((result as any)?.skipped);

      if (wasSkipped) {
        alert('No feedback updates were applied.');
        return;
      }

      alert('Feedback configuration saved successfully!');
    } catch (error) {
      console.error("Feedback config update error:", error);
      alert(`Failed to save feedback configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsFeedbackSaving(false);
    }
  };

  const onSubmitAuditConfig = async (data: AuditConfigValues) => {
    setIsAuditSaving(true);
    try {
      console.info('Security configuration submission ignored (not supported yet):', data);
      alert('Security notifications are currently disabled. Configuration updates will be available soon.');
    } catch (error) {
      console.error("Audit config update error:", error);
      alert(`Failed to save audit configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAuditSaving(false);
    }
  };

  const inviteMember = () => {
    navigate('/members');
  };

  const resolvePlanId = (rawPlan?: string | null): PlanId => {
    const normalized = (rawPlan ?? '').toString().trim().toUpperCase();

    if (normalized.includes('ENTERPRISE')) return 'ENTERPRISE';
    if (normalized.includes('PROFESS')) return 'PROFESSIONAL';
    if (normalized.includes('START')) return 'STARTER';
    if (normalized.includes('FREE')) return 'FREE';
    if (!normalized) return 'FREE';

    return normalized as PlanId;
  };

  const upgradePlan = () => {
    setPlanUpgradeOpen(true);
  };

  const persistPendingBilling = React.useCallback(
    ({
      organizationId,
      planId,
      sessionId,
      source,
      organizationDetails,
    }: {
      organizationId?: string | null;
      planId: PlanId;
      sessionId?: string | null;
      source: 'payment_link' | 'checkout_session';
      organizationDetails?: {
        name?: string | null;
        description?: string | null;
        industry?: string | null;
        companySize?: string | null;
      };
    }) => {
      try {
        sessionStorage.setItem(
          'pendingBilling',
          JSON.stringify({
            organizationId: organizationId ?? null,
            planId,
            sessionId: sessionId ?? null,
            source,
            organization: organizationDetails
              ? {
                  name: organizationDetails.name ?? undefined,
                  description: organizationDetails.description ?? undefined,
                  industry: organizationDetails.industry ?? undefined,
                  companySize: organizationDetails.companySize ?? undefined,
                }
              : undefined,
            storedAt: new Date().toISOString(),
          }),
        );
      } catch (storageError) {
        console.warn('Failed to persist pending billing info', storageError);
      }
    },
    [],
  );

  const updatePayment = async () => {
    try {
      const result = await createBillingPortalMutation.mutateAsync({ organizationId: organization?.id });

      // Redirect to Stripe Customer Portal
      if (result.data?.url) {
        window.location.href = result.data.url;
      }
    } catch (error) {
      console.error('Portal error:', error);
      alert('Unable to open billing portal. Please ensure you have an active subscription.');
    }
  };

  const handleLogoRemove = async () => {
    setIsLogoRemoving(true);
    try {
      if (!organization?.id) {
        throw new Error('Organization not found');
      }

      await removeLogoMutation.mutateAsync({ organizationId: organization.id });
      setOrganizationLogo(null);
      alert("Organization logo removed successfully!");
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = null;
      }
      setLogoPreviewUrl(null);
    } catch (error) {
      console.error("Logo removal error:", error);
      alert(`Failed to remove organization logo: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLogoRemoving(false);
    }
  };

  const triggerLogoUpload = () => {
    if (isLogoUploading) {
      return;
    }
    fileInputRef.current?.click();
  };

  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!organization?.id) {
      alert('Organization not found. Please try reloading the page.');
      event.target.value = '';
      return;
    }

    if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
      alert('Please upload a PNG or SVG logo.');
      event.target.value = '';
      return;
    }

    if (file.size > LOGO_MAX_SIZE_BYTES) {
      alert('Organization logo must be smaller than 2MB.');
      event.target.value = '';
      return;
    }

    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }

    const objectUrl = URL.createObjectURL(file);
    logoObjectUrlRef.current = objectUrl;
    setLogoPreviewUrl(objectUrl);

    const previousLogo = organizationLogo;

    setIsLogoUploading(true);

    try {
      const result = await uploadLogoMutation.mutateAsync({
        organizationId: organization.id,
        file,
      });

      const resolvedUrl = resolveAssetUrl(extractLogoUrl(result));

      if (resolvedUrl) {
        setOrganizationLogo(resolvedUrl);
        alert('Organization logo updated successfully!');
      } else {
        alert('Logo upload succeeded, but it may take a moment for the new logo to appear.');
        setOrganizationLogo(previousLogo ?? null);
      }

      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = null;
      }

      setLogoPreviewUrl(null);
    } catch (error) {
      console.error("Logo upload error:", error);
      alert(`Failed to update organization logo: ${error instanceof Error ? error.message : 'Unknown error'}`);

      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = null;
      }

      setLogoPreviewUrl(null);
      setOrganizationLogo(previousLogo ?? null);
    } finally {
      setIsLogoUploading(false);
      event.target.value = '';
    }
  };

  const handlePlanUpgrade = async (planId: PlanId) => {
    try {
      // Don't allow upgrade to FREE plan
      if (planId === 'FREE') {
        alert('Cannot downgrade to Free plan through this interface. Please contact support.');
        return;
      }

      // Use Payment Links by default (recommended approach)
      try {
        const result = await createPaymentLinkMutation.mutateAsync({
          planId,
          organizationId: organization?.id,
        });

        // Redirect to Stripe Payment Link
        if (result.data?.url) {
          persistPendingBilling({
            organizationId: organization?.id ?? null,
            planId,
            sessionId: null,
            source: 'payment_link',
            organizationDetails: organization
              ? {
                  name: organization.name ?? null,
                  description: organization.description ?? null,
                  industry: organization.industry ?? null,
                  companySize: organization.companySize ?? null,
                }
              : undefined,
          });
          window.location.href = result.data.url;
        }
      } catch (error) {
        // Fallback to checkout session if Payment Link fails
        const checkoutResult = await createCheckoutSessionMutation.mutateAsync({
          planId,
          organizationId: organization?.id,
          usePaymentLink: false, // Force custom checkout
        });

        // Redirect to custom Stripe Checkout
        if (checkoutResult.data?.url) {
          persistPendingBilling({
            organizationId: organization?.id ?? null,
            planId,
            sessionId: checkoutResult.data?.sessionId ?? null,
            source: 'checkout_session',
            organizationDetails: organization
              ? {
                  name: organization.name ?? null,
                  description: organization.description ?? null,
                  industry: organization.industry ?? null,
                  companySize: organization.companySize ?? null,
                }
              : undefined,
          });
          window.location.href = checkoutResult.data.url;
        }
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert(`Failed to upgrade plan: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Show loading state
  if (orgLoading || membersLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30">
        <div className="flex items-center justify-center py-20">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-border border-t-foreground"></div>
            <span className="text-muted-foreground">Loading organization...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30">
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">Organization not found</h2>
            <p className="text-muted-foreground">Unable to load organization data.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30">
      <div className="space-y-8 p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div>
            <h1 className="text-4xl font-bold text-foreground tracking-tight mb-2">Organization Settings</h1>
            <p className="text-lg text-muted-foreground">
              Manage your organization's information and configuration
            </p>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-3">
          <Tabs
            key="organization-tabs"
            value={activeTab}
            onValueChange={(value) => {
              console.log('Tabs onValueChange called:', { from: activeTab, to: value });
              setActiveTab(value);
            }}
            className="space-y-8"
          >
            <TabsList className="bg-background shadow-sm border border-border grid w-full grid-cols-5 mb-8 h-14 rounded-2xl p-2">
              <TabsTrigger value="general" className="flex items-center gap-2 rounded-xl font-semibold">
                <Building className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger value="team" className="flex items-center gap-2 rounded-xl font-semibold">
                <Users className="h-4 w-4" />
                Team
              </TabsTrigger>
              <TabsTrigger value="feedback" className="flex items-center gap-2 rounded-xl font-semibold">
                <Settings className="h-4 w-4" />
                Feedback
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2 rounded-xl font-semibold">
                <Shield className="h-4 w-4" />
                Security
              </TabsTrigger>
              <TabsTrigger value="billing" className="flex items-center gap-2 rounded-xl font-semibold">
                <CreditCard className="h-4 w-4" />
                Billing
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general">
              <div className="bg-background shadow-sm border border-border p-8 rounded-2xl">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl flex items-center justify-center">
                    <Building className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Organization Information</h3>
                    <p className="text-muted-foreground">
                      Basic information about your organization
                    </p>
                  </div>
                </div>
                  <Form {...organizationForm}>
                    <form onSubmit={organizationForm.handleSubmit(onSubmitOrganization)} className="space-y-6">
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={organizationForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Organization Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter organization name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={organizationForm.control}
                          name="industry"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Industry</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select industry" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {industries.map((industry) => (
                                    <SelectItem key={industry.value} value={industry.value}>
                                      {industry.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={organizationForm.control}
                          name="companySize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Size</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select company size" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {companySizes.map((size) => (
                                    <SelectItem key={size.value} value={size.value}>
                                      {size.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={organizationForm.control}
                          name="timeZone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Time Zone</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select time zone" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {timeZones.map((zone) => (
                                    <SelectItem key={zone.value} value={zone.value}>
                                      {zone.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={organizationForm.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website</FormLabel>
                            <FormControl>
                              <Input type="url" placeholder="https://example.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={organizationForm.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Description</FormLabel>
                            <FormControl>
                              <Textarea placeholder="Tell us about your organization..." rows={3} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="flex justify-end pt-6 border-t border-border">
                        <Button
                          type="submit"
                          disabled={isOrganizationSaving}
                          className="bg-primary hover:bg-primary text-white rounded-xl h-12 px-8 shadow-sm transition-colors disabled:opacity-50"
                        >
                          {isOrganizationSaving ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Save Changes
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </Form>
              </div>
            </TabsContent>

            {/* Team Tab */}
            <TabsContent value="team">
              <div className="bg-background shadow-sm border border-border p-8 rounded-2xl">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                      <Users className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">Team Management</h3>
                      <p className="text-muted-foreground">
                        Overview of your team members and roles
                      </p>
                    </div>
                  </div>
                  <Button onClick={inviteMember} className="bg-primary hover:bg-primary text-white rounded-xl shadow-sm transition-colors">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Invite Member
                  </Button>
                </div>
                {/* Team Stats */}
                <div className="grid grid-cols-4 gap-6 mb-8">
                  <div className="text-center p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100">
                    <div className="text-3xl font-bold text-primary mb-2">
                      {totalMembersCount}
                    </div>
                    <div className="text-muted-foreground font-medium">Total Members</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-blue-100">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {managerCount}
                    </div>
                    <div className="text-muted-foreground font-medium">Admins &amp; Managers</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-100">
                    <div className="text-3xl font-bold text-primary mb-2">
                      {activeMembersCount}
                    </div>
                    <div className="text-muted-foreground font-medium">Active Members</div>
                  </div>
                  <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl border border-yellow-100">
                    <div className="text-3xl font-bold text-orange-600 mb-2">
                      {pendingInvitesCount}
                    </div>
                    <div className="text-muted-foreground font-medium">Pending Invites</div>
                  </div>
                </div>

                {/* Recent Members */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xl font-bold text-foreground">Recent Members</h4>
                    <Link to="/members" className="text-primary hover:text-primary font-medium transition-colors">
                      View All
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {recentMembers.map((member: any, index: number) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-background border border-border rounded-2xl hover:border-indigo-200 transition-all"
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12">
                            {member.user?.avatar ? (
                              <AvatarImage src={member.user.avatar} alt={`${member.user?.firstName ?? ''} ${member.user?.lastName ?? ''}`} />
                            ) : null}
                            <AvatarFallback className={`font-bold ${getAvatarColor(member.user?.firstName, member.user?.lastName)}`}>
                              {getInitials(member.user?.firstName, member.user?.lastName)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-bold text-foreground">{member.user?.firstName} {member.user?.lastName}</p>
                            <p className="text-muted-foreground">
                              {member.user?.jobTitle || formatRoleLabel(member.role)}
                              {getMemberTimelineLabel(member) ? ` • ${getMemberTimelineLabel(member)}` : ''}
                            </p>
                          </div>
                        </div>
                        <Badge
                          variant={member.isInvitation ? 'outline' : 'default'}
                          className={`rounded-lg px-3 py-1 font-semibold ${
                            member.isInvitation
                              ? 'bg-muted text-foreground border-border'
                              : 'bg-green-100 text-green-700 border-green-200'
                          }`}
                        >
                          {member.isInvitation ? 'Pending Invite' : 'Active'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Feedback Tab */}
            <TabsContent value="feedback">
              <div className="bg-background shadow-sm border border-border p-8 rounded-2xl">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl flex items-center justify-center">
                    <Settings className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Feedback Configuration</h3>
                    <p className="text-muted-foreground">
                      Configure feedback cycles and notification settings
                    </p>
                  </div>
                </div>
                  <Form {...feedbackForm}>
                    <form onSubmit={feedbackForm.handleSubmit(onSubmitFeedbackConfig)} className="space-y-6">
                      <div>
                        <h4 className="font-medium mb-4">Privacy & Permissions</h4>
                        <div className="space-y-4">
                          <FormField
                            control={feedbackForm.control}
                            name="anonymousFeedback"
                            render={({ field }) => (
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-sm font-medium">Anonymous Feedback</Label>
                                  <p className="text-xs text-muted-foreground">
                                    Allow team members to provide anonymous feedback
                                  </p>
                                </div>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end pt-6 border-t border-border">
                        <Button
                          type="submit"
                          disabled={isFeedbackSaving}
                          className="bg-primary hover:bg-primary text-white rounded-xl h-12 px-8 shadow-sm transition-colors disabled:opacity-50"
                        >
                          {isFeedbackSaving ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="mr-2 h-4 w-4" />
                              Save Configuration
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  </Form>
              </div>
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <div className="bg-background shadow-sm border border-border p-8 rounded-2xl">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-50 to-orange-50 rounded-xl flex items-center justify-center">
                    <Shield className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Security & Audit Configuration</h3>
                    <p className="text-muted-foreground">
                      Configure audit trail logging and security monitoring
                    </p>
                  </div>
                </div>
                <Form {...auditForm}>
                  <form onSubmit={auditForm.handleSubmit(onSubmitAuditConfig)} className="space-y-6">
                    <div>
                      <h4 className="font-medium mb-4">Audit Trail Settings</h4>
                      <div className="space-y-4">
                        <FormField
                          control={auditForm.control}
                          name="auditEnabled"
                          render={({ field }) => (
                            <div className="flex items-center justify-between">
                              <div>
                                <Label className="text-sm font-medium">Enable Audit Trail</Label>
                                <p className="text-xs text-muted-foreground">
                                  Track all user actions and system changes
                                </p>
                              </div>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </div>
                          )}
                        />

                        <FormField
                          control={auditForm.control}
                          name="auditRetentionDays"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Retention Period (Days)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min="30"
                                  max="2555"
                                  placeholder="90"
                                  {...field}
                                  onChange={(e) => field.onChange(parseInt(e.target.value))}
                                />
                              </FormControl>
                              <FormDescription>
                                How long to keep audit logs (30 days to 7 years). Default: 90 days.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={auditForm.control}
                          name="auditDetailLevel"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Detail Level</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select detail level" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="basic">Basic - Core actions only</SelectItem>
                                  <SelectItem value="detailed">Detailed - All user actions</SelectItem>
                                  <SelectItem value="comprehensive">Comprehensive - Full system activity</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormDescription>
                                Choose how much detail to capture in audit logs
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>


                    <div className="flex justify-between items-center pt-6 border-t border-border">
                      <div className="flex items-center gap-4">
                        <Link
                          to="/audit-trail"
                          className="text-primary hover:text-primary font-medium transition-colors"
                        >
                          View Audit Trail →
                        </Link>
                      </div>
                      <Button
                        type="submit"
                        disabled={isAuditSaving}
                        className="bg-primary hover:bg-primary text-white rounded-xl h-12 px-8 shadow-sm transition-colors disabled:opacity-50"
                      >
                        {isAuditSaving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="mr-2 h-4 w-4" />
                            Save Configuration
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing">
              <div className="bg-background shadow-sm border border-border p-8 rounded-2xl">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-foreground">Billing & Subscription</h3>
                    <p className="text-muted-foreground">
                      Manage your subscription and billing information
                    </p>
                  </div>
                </div>
                <div className="space-y-8">
                    {/* Current Plan */}
                    <div className="p-6 bg-primary/5 border border-primary/20 rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-primary">
                            {organization?.plan === 'FREE' ? 'Free Plan' :
                             organization?.plan === 'STARTER' ? 'Starter Plan' :
                             organization?.plan === 'PROFESSIONAL' ? 'Professional Plan' :
                             organization?.plan === 'ENTERPRISE' ? 'Enterprise Plan' : 'Free Plan'}
                          </h3>
                          <p className="text-sm text-primary/80">
                            {organization?.plan === 'FREE' ? 'Get started for free' :
                             organization?.plan === 'STARTER' ? 'Perfect for growing teams' :
                             organization?.plan === 'PROFESSIONAL' ? 'Advanced features for larger teams' :
                             organization?.plan === 'ENTERPRISE' ? 'Full features for enterprise' : 'Get started for free'}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            ${organization?.plan === 'FREE' ? '0' :
                              organization?.plan === 'STARTER' ? '29' :
                              organization?.plan === 'PROFESSIONAL' ? '79' :
                              organization?.plan === 'ENTERPRISE' ? '199' : '0'}
                            <span className="text-base font-normal">/month</span>
                          </div>
                          <p className="text-sm text-primary/80">
                            {organization?.plan === 'FREE' ? 'Forever free' : 'Billed monthly'}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {[
                          organization?.plan === 'FREE' ? '1 team' :
                          organization?.plan === 'STARTER' ? 'Up to 10 teams' :
                          organization?.plan === 'PROFESSIONAL' ? 'Up to 50 teams' :
                          organization?.plan === 'ENTERPRISE' ? 'Up to 100 teams' : '1 team',

                          organization?.plan === 'FREE' ? 'Basic feedback' :
                          organization?.plan === 'STARTER' ? 'Advanced feedback' :
                          organization?.plan === 'PROFESSIONAL' ? 'Advanced feedback & analytics' :
                          organization?.plan === 'ENTERPRISE' ? 'Custom feedback & advanced analytics' : 'Basic feedback',

                          organization?.plan === 'FREE' ? 'Email support' :
                          organization?.plan === 'STARTER' ? 'Priority support' :
                          organization?.plan === 'PROFESSIONAL' ? 'Priority support' :
                          organization?.plan === 'ENTERPRISE' ? 'Dedicated support' : 'Email support',

                          organization?.plan === 'FREE' ? 'Basic reporting' :
                          organization?.plan === 'STARTER' ? 'Advanced reporting' :
                          organization?.plan === 'PROFESSIONAL' ? 'Advanced analytics' :
                          organization?.plan === 'ENTERPRISE' ? 'Custom reporting & API access' : 'Basic reporting'
                        ].map((feature, index) => (
                          <div key={index} className="flex items-center text-sm text-primary/80">
                            <Check className="h-4 w-4 text-primary mr-2" />
                            {feature}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-3">
                        {organization?.plan !== 'ENTERPRISE' && (
                          <Button onClick={upgradePlan}>Upgrade Plan</Button>
                        )}
                        {organization?.plan !== 'FREE' && (
                          <Button variant="outline" onClick={updatePayment}>
                            Manage Billing
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Billing Management Note */}
                    {organization?.plan !== 'FREE' && (
                      <div className="p-6 bg-blue-50/50 border border-blue-200/50 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <Shield className="h-5 w-5 text-blue-600" />
                          <h4 className="text-lg font-semibold text-blue-900">Billing Management</h4>
                        </div>
                        <p className="text-blue-700 mb-4">
                          All billing, payment methods, invoices, and subscription changes are securely managed by Stripe.
                          Use the "Manage Billing" button above to access your billing portal.
                        </p>
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <Check className="h-4 w-4" />
                          <span>View and download invoices</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <Check className="h-4 w-4" />
                          <span>Update payment methods</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <Check className="h-4 w-4" />
                          <span>Manage subscription settings</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <Check className="h-4 w-4" />
                          <span>Cancel or modify plans</span>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-8">
          {/* Organization Logo */}
          <div className="bg-background shadow-sm border border-border p-8 rounded-2xl text-center">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-muted/50 to-muted rounded-xl flex items-center justify-center">
                <Building className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Organization Logo</h3>
            </div>
            <Avatar className="h-24 w-24 mx-auto mb-6">
              {logoPreviewUrl || organizationLogo ? (
                <AvatarImage src={logoPreviewUrl ?? organizationLogo ?? undefined} alt={organization?.name || 'Organization'} />
              ) : (
                <AvatarFallback className={`text-2xl font-bold ${getAvatarColor(organization?.name || 'Organization', '')}`}>
                  {getInitials(organization?.name || 'AC', '')}
                </AvatarFallback>
              )}
            </Avatar>
            <div className="mb-6">
              <p className="text-xl font-bold text-foreground mb-1">{organization?.name || 'Organization'}</p>
              <p className="text-muted-foreground">Technology Company</p>
            </div>
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/svg+xml"
                className="hidden"
                onChange={handleLogoFileChange}
              />
              <Button
                className="w-full bg-primary hover:bg-primary text-white rounded-xl shadow-sm transition-colors"
                onClick={triggerLogoUpload}
                disabled={isLogoUploading}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isLogoUploading ? 'Uploading…' : 'Upload Logo'}
              </Button>
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={handleLogoRemove}
                disabled={!organizationLogo || isLogoRemoving || isLogoUploading}
              >
                <Trash className="mr-2 h-4 w-4" />
                {isLogoRemoving ? 'Removing…' : 'Remove Logo'}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              PNG or SVG. Max size 2MB.
            </p>
          </div>

        </div>
      </div>
    </div>
    <PlanUpgradeModal
      isOpen={planUpgradeOpen}
      onClose={() => setPlanUpgradeOpen(false)}
      onUpgrade={handlePlanUpgrade}
      currentPlan={resolvePlanId(organization?.plan)}
      organizationUsage={organizationUsageData?.data}
    />
    </div>
  );
}
